/**
 * D349, D350: the compute theme's map kit (`MapKit` in `iso-kit.ts`): a garage start-up growing
 * into a hyperscale campus. Its plots are the farm's places under the compute producers' ids
 * (the shared economy keeps every position, D351), every one drawn as a building plot that grows
 * tall as well as wide (`iso-kit-compute-art.ts`). The campus stands on concrete and tarmac with
 * grass verges inside a mesh fence; the farm's track is a road with a cable tray beside it; the
 * country round it is a business park with its car parks, a motorway, and a city skyline at the
 * back. Seasons and the hour stay (D349): snow on the roofs in winter, lit windows and racks at
 * night. Its 3D half is `computeThree` in `idle/three/compute.ts`.
 */
import { getIdleContent } from '@hedgerow/data';
import type { CountryItem, WorldLayout } from '../idle/map.ts';
import {
  ASSET_PALETTE,
  GOOD_WEATHER,
  LANDSCAPE,
  PHASE_LIGHT,
  PHASE_TIME,
  FIELD_CYCLE,
  READY_BADGE,
  SNOW,
} from '../idle/appearance.ts';
import { THEME_MAP_COLOURS } from '../idle/assets/idle-worlds-assets.js';
import type { IsoBuildingAt, Standing } from './IsoWorldMap.tsx';
import {
  TH,
  TW,
  alpha,
  depth,
  frontCorner,
  isPlan,
  mix,
  palette,
  parcelBoundaries,
  polyPath,
  project,
  rectPts,
  rnd,
  scatter,
  shade,
  tint,
  unionBounds,
  unionPath,
  assetLevel,
  heartPlan,
  type Bounds,
  type Heart,
  type IsoParcel,
  type MapTokens,
  type Palette,
  type Point,
  WINTER_COVER,
} from './iso.ts';
import { paintProp, type Prop, type Puff, type Ripple, pondRipples } from './iso-paint.ts';
import type { FlatEdges, FlatScenery, MapKit, PlotSlot } from './iso-kit.ts';
import { SLOTS as FARM_SLOTS, CHAIN_BUILDINGS as FARM_CHAINS } from './iso-kit-farm.ts';
import {
  C,
  CABLES,
  FENCE_INFILL,
  FENCING,
  FLOOR,
  PARK,
  SIGNS,
  buildingUnits,
  hashXY,
  markUnits,
  unitTop,
  WINTER,
  busyShare,
  litShare,
  sweepOn,
  type FaceMarks,
  type Look,
  type Piece,
  type Unit,
} from './iso-kit-compute-art.ts';

/* ---------- the plots and chain buildings: the farm's places, by position (D351) ---------- */

/**
 * The farm's slot for each position, under the compute producer at that position: the same
 * place, size and reserve, every one a building plot that fills from its back corner (`field`),
 * with nothing of the farm's drawn on it. The land beside the farm keeps its own id's.
 */
function computePlots(): Record<string, PlotSlot> {
  const farm = getIdleContent('farm'),
    compute = getIdleContent('compute');
  const landOf = new Map(farm.map.lands.map((l, i) => [l.id, compute.map.lands[i]?.id ?? l.id]));
  const out: Record<string, PlotSlot> = {};
  farm.producers.forEach((p, i) => {
    const id = compute.producers[i]?.id;
    const s = FARM_SLOTS[p.id];
    if (!id || !s) return;
    out[id] = {
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h,
      kind: 'field',
      ...(s.chainReserve !== undefined ? { chainReserve: s.chainReserve } : {}),
      ...(s.land ? { land: landOf.get(s.land) ?? s.land } : {}),
    };
  });
  return out;
}

/** Each chain's building where the farm's chain at that position stands. */
function computeChains(): Record<string, IsoBuildingAt> {
  const farm = getIdleContent('farm'),
    compute = getIdleContent('compute');
  const out: Record<string, IsoBuildingAt> = {};
  farm.chains.forEach((c, i) => {
    const id = compute.chains[i]?.id;
    const b = FARM_CHAINS[c.id];
    if (id && b) out[id] = { ...b, kind: 'shed', use: 'processing' };
  });
  return out;
}

/* ---------- the palette ---------- */

/** D381: the green the campus's lawns and the park's verges lean to: fresher than `PARK.lawn`. */
const LAWN = mix(PARK.lawn, '#5FB86A', 0.5);

/** D349: the campus's own ground, through the season's wash; the hour and the weather as ever. */
function computePalette(
  season: string,
  time: string,
  tokens?: MapTokens | null,
  weather?: Parameters<typeof palette>[3],
): Palette {
  const P = palette(season, time, tokens, weather);
  const [g, gt] = P.S.ground;
  const frost = !!P.S.frost;
  return {
    ...P,
    soil: mix(C.concrete, g, gt * 0.4),
    yard: mix(C.paving, g, gt * 0.3),
    road: mix(C.tarmac, g, 0.06),
    track: mix(C.tarmac, g, 0.06),
    waste: mix(C.gravel, g, gt * 0.3),
    hedge: C.fence,
    /* D381: the campus's grass, and the business park's round it, both maps. Mown, watered
       lawns: a fresh, soft green in every season (the farm's autumn and winter meadows read
       khaki on a campus), under the snow too. */
    meadow: frost
      ? mix(mix(P.meadow, LAWN, 0.55), SNOW.colour, WINTER.grass)
      : mix(P.meadow, LAWN, 0.6),
    pasture: frost
      ? mix(mix(P.pasture, LAWN, 0.5), SNOW.colour, WINTER.grass * 0.8)
      : mix(P.pasture, LAWN, 0.55),
    water: mix(C.pond, P.water, 0.4),
  };
}

const NIGHT = new Set(['Night', 'Dusk']);
const isNight = (P: Palette): boolean => NIGHT.has(P.T.label);
export const lookOf = (P: Palette): Look => ({ night: isNight(P), winter: !!P.S.frost });

/* ---------- D349: the country round the campus ---------- */

const overlaps = (a: Bounds, b: Bounds): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

let landscapeCache: { key: string; out: CountryItem[] } | null = null;

/**
 * The country round the campus, from its seed: a business park of office blocks, sheds, car
 * parks and planted squares on a grid of service roads; a motorway along the far side; a city's
 * skyline beyond the back corner; the road out from the gate. Kept clear of the land in use, a
 * strip round it and the land beside it, so the campus growing only takes the nearest lots. A
 * kind's scenery brings the city nearer (`village`), adds towers (`houses`) and hills.
 *
 * Its items: `croad` a road (`w` or `h` its width), `cmway` the motorway (`r` 1 when it runs
 * along y), `clot` a lot (`k` office, shed, park or green; `s` its storeys), `ctower` a tower of
 * the skyline (`s` its height), `ctree` a tree (`s` its size), and the shared `chills`.
 */
export function computeLandscape(L: WorldLayout, plot: Bounds, lands: Bounds[]): CountryItem[] {
  const key = JSON.stringify([L.seed, plot, lands, L.gate, L.scenery]);
  if (landscapeCache?.key === key) return landscapeCache.out;
  const sc = L.scenery;
  const S = L.seed >>> 0;
  const R = LANDSCAPE.reach;
  const out: CountryItem[] = [];
  // The campus's country is near flat: a kind's hills, much gentler than the farm's.
  out.push({ t: 'chills', x: plot.x - R, y: plot.y - R, w: 0, h: 0, s: 0.3 * sc.hills });
  const pad = (b: Bounds, n: number): Bounds => ({
    x: b.x - n,
    y: b.y - n,
    w: b.w + 2 * n,
    h: b.h + 2 * n,
  });
  const keep: Bounds[] = [pad(plot, 2), ...lands.map((b) => pad(b, 2))];
  // The road out of the gate, on to the edge of the country.
  const g = L.gate;
  const dir: [number, number] = g.y >= L.height ? [0, 1] : g.x <= 0 ? [-1, 0] : [1, 0];
  const far = R + Math.max(plot.w, plot.h);
  const road: Bounds =
    dir[1] === 0
      ? { x: dir[0] < 0 ? g.x - far : g.x, y: g.y - 0.45, w: far, h: 0.9 }
      : { x: g.x - 0.45, y: g.y, w: 0.9, h: far };
  out.push({ t: 'croad', ...road });
  keep.push(pad(road, 1));
  // The motorway, along the far side (back left at rest), its verges kept clear.
  const mx = Math.round(plot.x - 6 - hashXY(S, 1, 3) * 3);
  const mway: Bounds = { x: mx - 1.6, y: plot.y - R, w: 3.2, h: plot.h + 2 * R };
  out.push({ t: 'cmway', ...mway, r: 1 });
  keep.push(pad(mway, 0.6));
  // The skyline, past the motorway, beyond the back corner; a city site's nearer and taller.
  const towers = 9 + Math.round(sc.houses * 1.5);
  const dist = 15 * sc.village;
  for (let i = 0; i < towers; i++) {
    const a = (0.08 + 0.84 * hashXY(S, i, 11)) * (Math.PI / 2);
    const d = dist + hashXY(S, i, 12) * 8;
    const x = plot.x + plot.w * 0.2 - Math.cos(a) * d,
      y = plot.y + plot.h * 0.2 - Math.sin(a) * d;
    const w = 1 + hashXY(S, i, 13) * 1.6,
      h = 1 + hashXY(S, i, 14) * 1.6;
    const b: Bounds = { x, y, w, h };
    if (keep.some((k) => overlaps(k, b)) || overlaps(pad(mway, 0.6), b)) continue;
    const tall = (2.5 + hashXY(S, i, 15) * 5.5) * (sc.houses > 0 ? 1.25 : 1);
    out.push({ t: 'ctower', x, y, w, h, s: tall, k: String(Math.floor(hashXY(S, i, 16) * 4)) });
    keep.push(b);
  }
  // The business park: lots on a grid of service roads round the campus.
  const lot = 5,
    gap = 1;
  const cell = lot + gap;
  const x0 = Math.floor((plot.x - R) / cell) * cell,
    y0 = Math.floor((plot.y - R) / cell) * cell;
  for (let x = x0; x < plot.x + plot.w + R; x += cell)
    for (let y = y0; y < plot.y + plot.h + R; y += cell) {
      const b: Bounds = { x, y, w: lot, h: lot };
      if (keep.some((k) => overlaps(k, b))) continue;
      const roll = hashXY(x, y, S ^ 0x31);
      const k = roll < 0.4 ? 'office' : roll < 0.6 ? 'shed' : roll < 0.8 ? 'park' : 'green';
      out.push({ t: 'clot', ...b, k, s: 1 + Math.floor(hashXY(x, y, S ^ 0x32) * 4) });
      out.push({ t: 'croad', x: x - gap, y: y + lot, w: cell, h: gap });
      out.push({ t: 'croad', x: x + lot, y: y - gap, w: gap, h: cell });
      const size = (q: number) => 0.8 + hashXY(q, x + y, S ^ 0x35) * 0.5;
      if (k === 'green')
        for (let q = 0; q < 6; q++)
          out.push({
            t: 'ctree',
            x: x + 0.5 + hashXY(x + q, y, S ^ 0x33) * (lot - 1),
            y: y + 0.5 + hashXY(x, y + q, S ^ 0x34) * (lot - 1),
            w: 0,
            h: 0,
            s: size(q),
          });
      else {
        // D381: a row of street trees on the verge along the lot's road; a car park's trees
        // run down its side as well.
        for (let q = 0; q < 3; q++)
          out.push({
            t: 'ctree',
            x: x + 0.8 + q * 1.7,
            y: y + lot - 0.06,
            w: 0,
            h: 0,
            s: size(q),
          });
        if (k === 'park')
          for (let q = 1; q < 3; q++)
            out.push({
              t: 'ctree',
              x: x + lot - 0.06,
              y: y + q * 1.7,
              w: 0,
              h: 0,
              s: size(q + 4),
            });
      }
    }
  landscapeCache = { key, out };
  return out;
}

/* ---------- D381: the business park's lots, for both renderers ---------- */

/** A parked car: its middle, its paint, and whether it lies along x rather than y. */
export interface ParkedCar {
  x: number;
  y: number;
  c: string;
  along: boolean;
}

/**
 * A lot's building, its parked cars, its lamp columns and the bays painted on its tarmac. The
 * colours are the park's own, unhazed: each renderer hazes them as it hazes the country. The
 * flat map draws the building's `pieces` only; the 3D map its `fine` detail, the cars and the
 * lamps as well.
 */
export interface ParkLot {
  unit: Unit | null;
  cars: ParkedCar[];
  lamps: Array<[number, number]>;
  /** Tarmac with bays painted across it: x, y, w, h, the bays' width. */
  bays: Array<{ x: number; y: number; w: number; h: number }>;
}

let parkCache: { items: readonly CountryItem[]; out: Map<CountryItem, ParkLot> } | null = null;

/** D381: a lot of the business park, as both renderers draw it, the same from its place. */
export function parkLot(c: CountryItem): ParkLot {
  const seed = hashXY(c.x, c.y, 71);
  const pick = <T>(list: readonly T[], salt: number): T =>
    list[Math.floor(hashXY(c.x, c.y, salt) * list.length) % list.length] as T;
  const cars: ParkedCar[] = [];
  const lamps: Array<[number, number]> = [];
  const bays: ParkLot['bays'] = [];
  /** A row of bays across x, cars nose in along y, about `full` of them taken. */
  const row = (x0: number, x1: number, y0: number, full: number) => {
    bays.push({ x: x0, y: y0, w: x1 - x0, h: 1 });
    for (let x = x0; x + 0.5 <= x1 + 1e-6; x += 0.5)
      if (hashXY(x, y0, 73) < full)
        cars.push({
          x: x + 0.25,
          y: y0 + 0.5,
          c: pick(PARK.cars, Math.round(x * 13 + y0 * 7)),
          along: false,
        });
  };
  if (c.k === 'park') {
    const full = 0.55 + hashXY(c.x, c.y, 74) * 0.35;
    row(c.x + 0.5, c.x + c.w - 0.5, c.y + 0.6, full);
    row(c.x + 0.5, c.x + c.w - 0.5, c.y + c.h - 1.6, full);
    for (const f of [0.22, 0.5, 0.78]) lamps.push([c.x + c.w * f, c.y + c.h / 2]);
    return { unit: null, cars, lamps, bays };
  }
  if (c.k !== 'office' && c.k !== 'shed') return { unit: null, cars, lamps, bays };
  const pieces: Piece[] = [];
  const fine: Piece[] = [];
  const roofTone = pick(PARK.roofs, 75);
  let bx: number, by: number, bw: number, bd: number, h: number;
  if (c.k === 'office') {
    const floors = c.s ?? 1;
    bw = 2.4 + hashXY(c.x, c.y, 76) * 1.4;
    bd = 1.5 + hashXY(c.x, c.y, 77) * 1.1;
    bx = c.x + 0.5 + hashXY(c.x, c.y, 78) * (c.w - 1 - bw);
    by = c.y + 0.5;
    h = floors * FLOOR + 0.12;
    const style = seed < 0.35 ? 'glass' : seed < 0.65 ? 'brick' : 'clad';
    const wall = pick(PARK[style], 79);
    // How much of the office works late: its share of windows lit at night.
    const lit = 0.3 + hashXY(c.x, c.y, 83) * 0.4;
    const win =
      style === 'glass'
        ? { c: PARK.pane, cols: Math.max(4, Math.round(bw * 1.8)), z0: 0.04, z1: 0.98 }
        : style === 'brick'
          ? { c: PARK.sill, cols: Math.max(3, Math.round(bw * 1.5)), z0: 0.08, z1: 0.92 }
          : { c: C.window, cols: Math.max(2, Math.round(bw * 1.1)), z0: 0.1, z1: 0.9 };
    // D382: the 3D map's facade for the style.
    const face = style === 'glass' ? 'curtain' : style === 'brick' ? 'punched' : 'band';
    pieces.push({
      s: 'box',
      x: bx,
      y: by,
      w: bw,
      d: bd,
      z: 0,
      h,
      c: wall,
      top: roofTone,
      roof: true,
      face,
      marks: {
        kind: 'win',
        rows: floors,
        cols: win.cols,
        c: win.c,
        on: C.lit,
        lit,
        z0: win.z0,
        z1: win.z1,
      },
    });
    // A lower wing to one side, on about half the offices.
    if (floors > 1 && hashXY(c.x, c.y, 80) < 0.5 && bx + bw + 0.9 < c.x + c.w - 0.3) {
      const ww = Math.min(1.2, c.x + c.w - 0.3 - (bx + bw));
      pieces.push({
        s: 'box',
        x: bx + bw,
        y: by + 0.2,
        w: ww,
        d: bd - 0.4,
        z: 0,
        h: (floors - 1) * FLOOR + 0.12,
        c: wall,
        top: roofTone,
        roof: true,
        face,
        marks: {
          kind: 'win',
          rows: floors - 1,
          cols: 3,
          c: win.c,
          on: C.lit,
          lit,
          z0: win.z0,
          z1: win.z1,
        },
      });
    }
    // The entrance canopy, white, over the front door.
    pieces.push({
      s: 'box',
      x: bx + bw * 0.4,
      y: by + bd,
      w: 0.5,
      d: 0.18,
      z: 0.24,
      h: 0.03,
      c: PARK.frame,
    });
    // The car park in front, as full as the office is busy.
    const front = by + bd + 0.35;
    if (front + 1 <= c.y + c.h - 0.3) row(c.x + 0.5, c.x + c.w - 0.5, front, 0.45 + floors * 0.1);
  } else {
    bw = c.w - 1.2 - hashXY(c.x, c.y, 76) * 0.6;
    bd = 2.2 + hashXY(c.x, c.y, 77) * 0.6;
    bx = c.x + 0.6;
    by = c.y + 0.5;
    h = 0.55 + hashXY(c.x, c.y, 78) * 0.3;
    const wall = pick(PARK.shed, 79);
    pieces.push({
      s: 'box',
      x: bx,
      y: by,
      w: bw,
      d: bd,
      z: 0,
      h,
      c: wall,
      top: roofTone,
      roof: true,
      face: 'ribbed',
      marks: { kind: 'grille', rows: 3, cols: 1, c: shade(wall, 0.1), z0: 0.05, z1: 0.95 },
    });
    // Roller doors along the front, and the office at the end, glazed.
    const n = 2 + Math.floor(hashXY(c.x, c.y, 80) * 2);
    for (let i = 0; i < n; i++)
      pieces.push({
        s: 'box',
        x: bx + bw * 0.35 + i * 0.5,
        y: by + bd - 0.005,
        w: 0.36,
        d: 0.025,
        z: 0,
        h: Math.min(0.42, h - 0.1),
        c: PARK.door,
        marks: { kind: 'grille', rows: 5, cols: 1, c: C.flue, front: true },
      });
    pieces.push({
      s: 'box',
      x: bx,
      y: by + bd - 0.2,
      w: 0.8,
      d: 0.45,
      z: 0,
      h: 2 * FLOOR + 0.08,
      c: PARK.frame,
      top: roofTone,
      roof: true,
      face: 'band',
      marks: { kind: 'win', rows: 2, cols: 3, c: C.window, on: C.lit, z0: 0.1, z1: 0.9 },
    });
    for (let i = 0; i < 3; i++)
      fine.push({
        s: 'box',
        x: bx + bw * (0.3 + i * 0.22),
        y: by + 0.25,
        w: 0.16,
        d: bd - 0.5,
        z: h,
        h: 0.02,
        c: PARK.rooflight,
      });
    const front = by + bd + 0.35;
    if (front + 1 <= c.y + c.h - 0.3) row(c.x + 0.5, c.x + 2.5, front, 0.6);
  }
  // The roof: a parapet, plant and a vent or two, a mast on a tall one.
  const t = 0.04;
  for (const [x, y, w, d] of [
    [bx, by, bw, t],
    [bx, by + bd - t, bw, t],
    [bx, by, t, bd],
    [bx + bw - t, by, t, bd],
  ] as const)
    fine.push({ s: 'box', x, y, w, d, z: h, h: 0.05, c: roofTone });
  const units = 1 + Math.floor(hashXY(c.x, c.y, 81) * 3);
  for (let i = 0; i < units; i++)
    fine.push({
      s: 'box',
      x: bx + 0.3 + i * 0.42,
      y: by + 0.3 + hashXY(c.x + i, c.y, 82) * (bd - 0.8),
      w: 0.32,
      d: 0.24,
      z: h,
      h: 0.12,
      c: PARK.plant[i % 2] as string,
    });
  fine.push({ s: 'box', x: bx + bw - 0.45, y: by + 0.3, w: 0.1, d: 0.1, z: h, h: 0.18, c: C.mid });
  if (c.k === 'office' && (c.s ?? 1) >= 3)
    fine.push({
      s: 'rod',
      a: [bx + bw - 0.3, by + bd - 0.3, h],
      b: [bx + bw - 0.3, by + bd - 0.3, h + 0.6],
      t: 0.03,
      c: C.steel,
    });
  return {
    unit: { x: bx, y: by, w: bw, d: bd, pieces, fine },
    cars,
    lamps,
    bays,
  };
}

/** D381: every lot of the park, kept while the country is the same. */
export function parkLots(items: readonly CountryItem[]): Map<CountryItem, ParkLot> {
  if (parkCache?.items === items) return parkCache.out;
  const out = new Map<CountryItem, ParkLot>();
  for (const c of items) if (c.t === 'clot') out.set(c, parkLot(c));
  parkCache = { items, out };
  return out;
}

/** D381: a piece's colours passed through `tone` (the haze), its lit colour left alone. */
export function tonePiece(q: Piece, tone: (c: string) => string): Piece {
  const out = { ...q, c: tone(q.c) } as Piece;
  if (out.s === 'box') {
    if (out.top) out.top = tone(out.top);
    if (out.marks) out.marks = { ...out.marks, c: tone(out.marks.c) };
  }
  return out;
}

/* ---------- painting the pieces in SVG ---------- */

/** One world unit up, in the flat map's pixels. */
export const ZPX = (TW / 2 / Math.SQRT1_2) * Math.cos(Math.PI / 6);
/** One world unit across, in the flat map's pixels, for a radius or a rod's thickness. */
const XPX = TW / 2 / Math.SQRT1_2;
const RX = TW / Math.SQRT2 / 1,
  RY = TH / Math.SQRT2;

const f1 = (n: number): string => n.toFixed(1);
const pts = (q: Point[]): string => polyPath(q);
const P3 = (x: number, y: number, z: number): Point => project(x, y, z * ZPX);

/** A colour hazed into the far country, as the farm's country is. */
const far = (P: Palette, c: string): string => P.far(c);

/** Snow on a roof in winter. */
const snowed = (c: string, L: Look, roof?: boolean): string =>
  L.winter && roof ? mix(c, SNOW.colour, WINTER.roof) : c;

function paintMarks(
  m: FaceMarks,
  a: [number, number],
  b: [number, number],
  z: number,
  h: number,
  L: Look,
  o: string[],
  salt: number,
): void {
  const lo = z + h * (m.z0 ?? 0.08),
    hi = z + h * (m.z1 ?? 0.92);
  /* The marks of one colour on a face are one path, so a rack's dozens of lights cost a few
     elements rather than dozens. A blinking light is in a path of its own colour and class. */
  const groups = new Map<string, string[]>();
  const add = (key: string, u0: number, u1: number, v0: number, v1: number) => {
    const q = (u: number, v: number): string => {
      const [px, py] = P3(a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, lo + (hi - lo) * v);
      return `${f1(px)} ${f1(py)}`;
    };
    const d = `M${q(u0, v0)}L${q(u1, v0)}L${q(u1, v1)}L${q(u0, v1)}Z`;
    const g = groups.get(key);
    if (g) g.push(d);
    else groups.set(key, [d]);
  };
  for (let j = 0; j < m.rows; j++)
    for (let i = 0; i < m.cols; i++) {
      const r = hashXY(salt + i * 0.37, j * 0.53 + z, 7);
      if (m.kind === 'led') {
        const busy = m.busy ?? 0;
        const on = r < (m.lit ?? 1);
        // D368: a lighter twinkle than the 3D kit's seeded groups: the busier the unit, the more
        // of its lit LEDs blink, on the faster, denser pace.
        const blink = on && m.blink && hashXY(salt, i + j * 7, 9) < 0.15 + busy * 0.35;
        const cls = blink ? (busy >= 0.5 ? 'cmp-blink-busy' : 'cmp-blink') : '';
        const u = (i + 0.35) / m.cols,
          v = (j + 0.3) / m.rows;
        add(
          `${on ? (m.on ?? C.green) : m.c}${cls ? `|${cls}` : ''}`,
          u,
          u + 0.3 / m.cols,
          v,
          v + 0.4 / m.rows,
        );
      } else if (m.kind === 'win')
        add(
          L.night && r < (m.lit ?? 0.72) ? (m.on ?? C.lit) : m.c,
          (i + 0.18) / m.cols,
          (i + 0.82) / m.cols,
          (j + 0.25) / m.rows,
          (j + 0.8) / m.rows,
        );
      else if (m.kind === 'band') {
        if (i === 0) add(m.on ?? m.c, 0.04, 0.96, (j + 0.3) / m.rows, (j + 0.7) / m.rows);
      } else
        add(m.c, (i + 0.08) / m.cols, (i + 0.92) / m.cols, (j + 0.35) / m.rows, (j + 0.6) / m.rows);
    }
  for (const [key, ds] of groups) {
    const [fill, cls] = key.split('|');
    const dur = cls === 'cmp-blink-busy' ? 0.6 : 1.8;
    o.push(
      `<path d="${ds.join('')}" fill="${fill}"${cls ? ` class="${cls}" style="animation-delay:-${(hashXY(salt, z, 3) * dur).toFixed(2)}s"` : ''}/>`,
    );
  }
}

/** A box: its two faces towards the eye and its top, the marks on the faces. */
function paintBox(q: Extract<Piece, { s: 'box' }>, P: Palette, L: Look, o: string[]): void {
  const { x, y, w, d, z, h } = q;
  const top = snowed(q.top ?? tint(q.c, 0.14), L, q.roof);
  if (isPlan()) {
    o.push(`<path d="${pts(rectPts(x, y, w, d))}" fill="${top}"/>`);
    return;
  }
  const W: Array<[number, number]> = [
    [x, y],
    [x + w, y],
    [x + w, y + d],
    [x, y + d],
  ];
  const fc = frontCorner(x, y, w, d);
  const Lc = W[(fc + 1) % 4] as [number, number],
    F = W[fc] as [number, number],
    Rc = W[(fc + 3) % 4] as [number, number];
  const face = (a: [number, number], b: [number, number], k: number) => {
    o.push(
      `<path d="${pts([P3(a[0], a[1], z), P3(b[0], b[1], z), P3(b[0], b[1], z + h), P3(a[0], a[1], z + h)])}" fill="${q.glow ? q.c : shade(q.c, k)}"/>`,
    );
    if (q.marks && h > 0.02) paintMarks(q.marks, a, b, z, h, L, o, a[0] * 3 + a[1] * 5);
  };
  face(Lc, F, 0.3);
  face(F, Rc, 0.12);
  o.push(`<path d="${pts(W.map(([a, b]) => P3(a, b, z + h)))}" fill="${q.glow ? q.c : top}"/>`);
}

function paintCyl(q: Extract<Piece, { s: 'cyl' }>, P: Palette, L: Look, o: string[]): void {
  const [cx, cb] = P3(q.x, q.y, q.z),
    [, ct] = P3(q.x, q.y, q.z + q.h);
  const rx = q.r * RX,
    ry = q.r * RY;
  const top = snowed(q.top ?? tint(q.c, 0.12), L, q.r > 0.2);
  if (isPlan()) {
    o.push(`<circle cx="${f1(cx)}" cy="${f1(cb)}" r="${f1(q.r * 32)}" fill="${top}"/>`);
    return;
  }
  const body = q.glow ? q.c : shade(q.c, 0.18);
  o.push(
    `<path d="M${f1(cx - rx)} ${f1(ct)} L${f1(cx - rx)} ${f1(cb)} A${f1(rx)} ${f1(ry)} 0 0 0 ${f1(cx + rx)} ${f1(cb)} L${f1(cx + rx)} ${f1(ct)} Z" fill="${body}"/>`,
  );
  if (!q.glow)
    o.push(
      `<path d="M${f1(cx + rx * 0.2)} ${f1(ct)} L${f1(cx + rx * 0.2)} ${f1(cb + ry * 0.98)} A${f1(rx)} ${f1(ry)} 0 0 0 ${f1(cx + rx)} ${f1(cb)} L${f1(cx + rx)} ${f1(ct)} Z" fill="${shade(q.c, 0.06)}"/>`,
    );
  o.push(
    `<ellipse cx="${f1(cx)}" cy="${f1(ct)}" rx="${f1(rx)}" ry="${f1(ry)}" fill="${q.glow ? tint(q.c, 0.3) : top}"/>`,
  );
}

/** A cooling tower: the waisted shell, its open mouth dark inside. */
function paintTower(q: Extract<Piece, { s: 'tower' }>, P: Palette, L: Look, o: string[]): void {
  const [cx, yb] = P3(q.x, q.y, q.z),
    [, ym] = P3(q.x, q.y, q.z + q.h * 0.7),
    [, yt] = P3(q.x, q.y, q.z + q.h);
  const rb = q.r * RX,
    rw = q.r * 0.62 * RX,
    rt = q.r * 0.7 * RX;
  const ryb = q.r * RY,
    ryt = q.r * 0.7 * RY;
  if (isPlan()) {
    o.push(`<circle cx="${f1(cx)}" cy="${f1(yb)}" r="${f1(rb)}" fill="${q.c}"/>`);
    o.push(`<circle cx="${f1(cx)}" cy="${f1(yb)}" r="${f1(rt)}" fill="${shade(q.c, 0.45)}"/>`);
    return;
  }
  const shell = (fill: string, side: 1 | -1 | 0) => {
    const l = side === 1 ? 0 : 1;
    const r = side === -1 ? 0 : 1;
    o.push(
      `<path d="M${f1(cx - rb * l)} ${f1(yb)} Q${f1(cx - rw * 0.8 * l)} ${f1(ym)} ${f1(cx - rt * l)} ${f1(yt)} L${f1(cx + rt * r)} ${f1(yt)} Q${f1(cx + rw * 0.8 * r)} ${f1(ym)} ${f1(cx + rb * r)} ${f1(yb)} A${f1(rb)} ${f1(ryb)} 0 0 1 ${f1(cx - rb * l)} ${f1(yb)} Z" fill="${fill}"/>`,
    );
  };
  shell(shade(q.c, 0.08), 0);
  shell(shade(q.c, 0.22), -1);
  o.push(
    `<ellipse cx="${f1(cx)}" cy="${f1(yt)}" rx="${f1(rt)}" ry="${f1(ryt)}" fill="${snowed(tint(q.c, 0.1), L, true)}"/>`,
  );
  o.push(
    `<ellipse cx="${f1(cx)}" cy="${f1(yt + 0.6)}" rx="${f1(rt * 0.86)}" ry="${f1(ryt * 0.8)}" fill="${shade(q.c, 0.55)}"/>`,
  );
}

function paintPanel(q: Extract<Piece, { s: 'panel' }>, L: Look, o: string[]): void {
  const c = snowed(q.c, L, true);
  const A = P3(q.x, q.y + q.d, q.z),
    B = P3(q.x + q.w, q.y + q.d, q.z),
    Cc = P3(q.x + q.w, q.y, q.z + q.rise),
    D = P3(q.x, q.y, q.z + q.rise);
  o.push(
    `<path d="${pts([A, B, Cc, D])}" fill="${c}" stroke="${shade(c, 0.3)}" stroke-width=".6"/>`,
  );
  if (q.c === C.solar && !L.winter)
    for (let i = 1; i < 4; i++) {
      const u = i / 4;
      const a = [A[0] + (B[0] - A[0]) * u, A[1] + (B[1] - A[1]) * u],
        b = [D[0] + (Cc[0] - D[0]) * u, D[1] + (Cc[1] - D[1]) * u];
      o.push(
        `<line x1="${f1(a[0] as number)}" y1="${f1(a[1] as number)}" x2="${f1(b[0] as number)}" y2="${f1(b[1] as number)}" stroke="${alpha(C.solarLine, 0.7)}" stroke-width=".6"/>`,
      );
    }
}

export function paintPiece(q: Piece, P: Palette, L: Look, o: string[]): void {
  switch (q.s) {
    case 'box':
      return paintBox(q, P, L, o);
    case 'cyl':
      return paintCyl(q, P, L, o);
    case 'tower':
      return paintTower(q, P, L, o);
    case 'panel':
      return paintPanel(q, L, o);
    case 'rod': {
      const a = P3(...q.a),
        b = P3(...q.b);
      o.push(
        `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(b[0])}" y2="${f1(b[1])}" stroke="${q.c}" stroke-width="${Math.max(0.8, q.t * XPX).toFixed(1)}" stroke-linecap="round"/>`,
      );
      return;
    }
    case 'disc': {
      const [cx, cy] = P3(q.x, q.y, q.z);
      o.push(
        `<ellipse cx="${f1(cx)}" cy="${f1(cy)}" rx="${f1(q.r * RX)}" ry="${f1(isPlan() ? q.r * RX : q.r * RY)}" fill="${q.c}"/>`,
      );
      return;
    }
    case 'ball': {
      const [cx, cy] = P3(q.x, q.y, q.z + q.r);
      const r = q.r * XPX;
      const c = snowed(q.c, L, true);
      o.push(
        `<circle cx="${f1(cx + r * 0.15)}" cy="${f1(cy + r * 0.15)}" r="${f1(r)}" fill="${shade(c, 0.25)}"/>` +
          `<circle cx="${f1(cx)}" cy="${f1(cy)}" r="${f1(r * 0.92)}" fill="${c}"/>` +
          `<circle cx="${f1(cx - r * 0.3)}" cy="${f1(cy - r * 0.3)}" r="${f1(r * 0.4)}" fill="${tint(c, 0.12)}" opacity=".8"/>`,
      );
    }
  }
}

/** A unit's pieces back to front: by their foot's height, then by depth at this turn. */
export function ordered(u: Unit): Piece[] {
  const key = (q: Piece): [number, number] => {
    if (q.s === 'rod')
      return [Math.min(q.a[2], q.b[2]), depth((q.a[0] + q.b[0]) / 2, (q.a[1] + q.b[1]) / 2)];
    if (q.s === 'box' || q.s === 'panel') return [q.z, depth(q.x + q.w / 2, q.y + q.d / 2)];
    return [q.z, depth(q.x, q.y)];
  };
  return [...u.pieces]
    .map((q) => ({ q, k: key(q) }))
    .sort((a, b) => a.k[0] - b.k[0] || a.k[1] - b.k[1])
    .map((e) => e.q);
}

export function paintUnit(u: Unit, P: Palette, o: string[]): void {
  const L = lookOf(P);
  // D345: the ready badge, where the map's tap looks for it (`READY_LIFT` over the middle).
  if (u.badge) {
    const { x, y, id, icon } = u.badge;
    paintProp({ k: 'ready', x, y, id, icon }, P, o);
  }
  if (!u.pieces.length) return;
  // The soft shade on the ground under it.
  if (!isPlan()) {
    const [cx, cy] = project(u.x + u.w / 2, u.y + u.d / 2);
    o.push(
      `<ellipse cx="${f1(cx + 3)}" cy="${f1(cy + 2)}" rx="${f1((u.w + u.d) * TW * 0.27)}" ry="${f1((u.w + u.d) * TH * 0.27)}" fill="rgba(30,32,36,.16)"/>`,
    );
  }
  for (const q of ordered(u)) paintPiece(q, P, L, o);
}

/* ---------- the country, the ground, the fence and the weather ---------- */

/**
 * D368: the page's own keyframes for the rack lights' twinkle, lighter than the 3D kit's seeded
 * groups (D366): a shared keyframe per pace, each lit LED given its own negative delay (seeded
 * from its position) so none ever starts in step with another. A busy unit's LEDs run the faster,
 * denser pace and stay lit longer; an idle unit's run the slow, sparse one. Frozen under reduced
 * motion.
 */
export const BLINK_CSS =
  '<style>' +
  '@keyframes cmp-blink{0%,30%{opacity:1}31%,100%{opacity:.2}}' +
  '.cmp-blink{animation:cmp-blink 1.8s steps(1,end) infinite}' +
  '@keyframes cmp-blink-busy{0%,70%{opacity:1}71%,100%{opacity:.2}}' +
  '.cmp-blink-busy{animation:cmp-blink-busy .6s steps(1,end) infinite}' +
  '@media (prefers-reduced-motion:reduce){.cmp-blink,.cmp-blink-busy{animation:none}}' +
  '</style>';

/** Whether a footprint lies wholly behind the land in use at this turn. */
function behind(b: Bounds, plot: Bounds): boolean {
  const corners = (q: Bounds) =>
    [
      [q.x, q.y],
      [q.x + q.w, q.y],
      [q.x, q.y + q.h],
      [q.x + q.w, q.y + q.h],
    ].map(([a, c]) => depth(a as number, c as number));
  return Math.max(...corners(b)) < Math.min(...corners(plot)) - 1;
}

function paintCountry(s: FlatScenery, P: Palette, o: string[]): void {
  const { far: plot, country } = s;
  o.push(BLINK_CSS);
  const R = LANDSCAPE.flatReach;
  o.push(
    `<path d="${pts(rectPts(plot.x - R - 4, plot.y - R - 4, plot.w + 2 * R + 8, plot.h + 2 * R + 8))}" fill="${far(P, P.pasture)}"/>`,
  );
  const edge = { x0: plot.x - R, y0: plot.y - R, x1: plot.x + plot.w + R, y1: plot.y + plot.h + R };
  const clip = (c: Bounds): Bounds | null => {
    const x0 = Math.max(c.x, edge.x0),
      y0 = Math.max(c.y, edge.y0),
      x1 = Math.min(c.x + c.w, edge.x1),
      y1 = Math.min(c.y + c.h, edge.y1);
    return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
  };
  const L = lookOf(P);
  const tarmac = far(P, P.road);
  // The ground: lots, roads, the motorway and its lanes.
  for (const c of country) {
    if (c.t === 'clot') {
      const b = clip({ x: c.x + 0.2, y: c.y + 0.2, w: c.w - 0.4, h: c.h - 0.4 });
      if (!b) continue;
      const fill =
        c.k === 'green' || c.k === 'office'
          ? far(P, P.meadow)
          : c.k === 'park'
            ? tarmac
            : far(P, mix(P.soil, P.road, 0.25));
      o.push(`<path d="${pts(rectPts(b.x, b.y, b.w, b.h))}" fill="${fill}"/>`);
      if (c.k === 'park')
        for (let i = 1; i < Math.floor(b.w / 0.5); i++) {
          const a = project(b.x + i * 0.5, b.y + b.h * 0.15),
            e = project(b.x + i * 0.5, b.y + b.h * 0.45);
          o.push(
            `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(e[0])}" y2="${f1(e[1])}" stroke="${alpha(C.line, 0.45)}" stroke-width=".8"/>`,
          );
        }
    } else if (c.t === 'croad' || c.t === 'cmway') {
      const b = clip(c);
      if (!b) continue;
      o.push(`<path d="${pts(rectPts(b.x, b.y, b.w, b.h))}" fill="${tarmac}"/>`);
      if (c.t === 'cmway')
        for (const f of [0.33, 0.5, 0.67]) {
          const a = project(b.x + b.w * f, b.y),
            e = project(b.x + b.w * f, b.y + b.h);
          o.push(
            `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(e[0])}" y2="${f1(e[1])}" stroke="${alpha(C.line, f === 0.5 ? 0.7 : 0.4)}" stroke-width="${f === 0.5 ? 2 : 0.9}" ${f === 0.5 ? '' : 'stroke-dasharray="6 8"'}/>`,
          );
        }
    }
  }
  // What stands: trees, and the lots' buildings and the skyline where they lie behind the
  // campus at this turn (one in front would be painted under the slab, so it lies flat).
  const stand: Array<{ d: number; u: Unit }> = [];
  for (const c of country) {
    if (c.t === 'ctree' || c.t === 'cwood') {
      if (!clip({ x: c.x - 0.01, y: c.y - 0.01, w: 0.02, h: 0.02 })) continue;
      const x = c.t === 'cwood' ? c.x + 0.5 : c.x,
        y = c.t === 'cwood' ? c.y + 0.5 : c.y;
      const s = c.s ?? 1;
      stand.push({
        d: depth(x, y),
        u: {
          x,
          y,
          w: 0,
          d: 0,
          pieces: [
            { s: 'cyl', x, y, r: 0.04, z: 0, h: 0.25 * s, c: far(P, '#6B5A44') },
            { s: 'ball', x, y, r: 0.28 * s, z: 0.18 * s, c: far(P, P.wood) },
          ],
        },
      });
    } else if (c.t === 'clot' && (c.k === 'office' || c.k === 'shed')) {
      // D381: the lot's building as the 3D map has it, without its fine detail.
      const lot = parkLot(c).unit;
      if (!lot) continue;
      const b = { x: lot.x, y: lot.y, w: lot.w, h: lot.d };
      if (!clip(b)) continue;
      const body = lot.pieces[0];
      const wall = far(P, body?.c ?? '#D9DCE0');
      if (!behind(b, plot)) {
        o.push(`<path d="${pts(rectPts(b.x, b.y, b.w, b.h))}" fill="${shade(wall, 0.1)}"/>`);
        continue;
      }
      stand.push({
        d: depth(b.x + b.w / 2, b.y + b.h / 2),
        u: { ...lot, fine: [], pieces: lot.pieces.map((q) => tonePiece(q, (k) => far(P, k))) },
      });
    } else if (c.t === 'ctower') {
      const b = { x: c.x, y: c.y, w: c.w, h: c.h };
      if (!clip(b) || !behind(b, plot)) continue;
      const wall = far(
        P,
        ['#B7C3D0', '#C9CDD2', '#8FA3B8', '#D4D0C8'][Number(c.k ?? 0) % 4] as string,
      );
      const h = c.s ?? 3;
      stand.push({
        d: depth(c.x + c.w / 2, c.y + c.h / 2),
        u: {
          ...b,
          d: c.h,
          pieces: [
            {
              s: 'box',
              x: c.x,
              y: c.y,
              w: c.w,
              d: c.h,
              z: 0,
              h,
              c: wall,
              top: shade(wall, 0.05),
              roof: true,
              marks: {
                kind: 'win',
                rows: Math.round(h / 0.3),
                cols: 4,
                c: shade(wall, 0.12),
                on: far(P, C.lit),
              },
            },
          ],
        },
      });
    }
  }
  stand.sort((a, b) => a.d - b.d);
  for (const s of stand) for (const q of ordered(s.u)) paintPiece(q, P, L, o);
}

/** The pad a producer's plot stands on, by what it is. */
const PAD: Record<string, string> = {
  laptops: C.tarmac,
  substations: C.gravel,
  solar_arrays: C.gravel,
  edge_sites: C.gravel,
  cooling_towers: C.pad,
  subsea_cables: C.gravel,
};

/** A parcel's ground: a building plot's pad, the yard's paving and its parking bays, grass. */
export function groundColour(p: IsoParcel, P: Palette): string {
  const winter = !!P.S.frost;
  const snow = (c: string, k = 1) => (winter ? mix(c, SNOW.colour, WINTER.hard * k) : c);
  switch (p.t) {
    case 'field':
      return snow(mix(PAD[p.id ?? ''] ?? C.pad, P.soil, 0.25), 0.8);
    case 'yard':
      return snow(P.yard, 0.8);
    case 'pond':
      return shade(P.water, 0.1);
    case 'waste':
      return snow(P.waste, 0.6);
    case 'meadow':
      return p.mark || p.ground ? snow(mix(C.paving, P.soil, 0.3), 0.8) : P.meadow;
    default:
      return snow(P.pasture);
  }
}

function paintGround(p: IsoParcel, P: Palette, o: string[]): void {
  if (!p.tiles.length) return;
  const fill = groundColour(p, P);
  o.push(
    `<path data-testid="iso-parcel" d="${unionPath(p.tiles)}" fill="${fill}" stroke="${shade(fill, 0.12)}" stroke-width="1"/>`,
  );
  const b = unionBounds(p.tiles);
  if (p.t === 'yard') {
    // Parking bays along the yard's front row, in the cable decoration's colour.
    const line = alpha(CABLES[p.path ?? ''] ?? C.line, 0.8);
    const y = b.y + b.h - 0.9;
    for (let x = b.x + 0.5; x < b.x + b.w; x += 0.5) {
      const a = project(x, y),
        e = project(x, y + 0.75);
      o.push(
        `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(e[0])}" y2="${f1(e[1])}" stroke="${line}" stroke-width="1"/>`,
      );
    }
  }
  if (p.t === 'pond') {
    // D349: the cooling pond's concrete lip.
    o.push(
      `<path d="${unionPath(p.tiles)}" fill="none" stroke="${C.concrete}" stroke-width="2.4"/>`,
    );
  }
}

/** The campus fence: posts and a light mesh, along a line on the ground. */
export function paintFence(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  c: string,
  o: string[],
  infill?: string,
): void {
  const hpx = 0.36 * ZPX;
  const a = project(x1, y1),
    b = project(x2, y2);
  if (isPlan()) {
    o.push(
      `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(b[0])}" y2="${f1(b[1])}" stroke="${c}" stroke-width="2"/>`,
    );
    return;
  }
  o.push(
    `<path d="M${f1(a[0])} ${f1(a[1])} L${f1(b[0])} ${f1(b[1])} L${f1(b[0])} ${f1(b[1] - hpx)} L${f1(a[0])} ${f1(a[1] - hpx)} Z" fill="${infill ? alpha(infill, 0.8) : alpha(c, 0.22)}"/>`,
  );
  o.push(
    `<line x1="${f1(a[0])}" y1="${f1(a[1] - hpx)}" x2="${f1(b[0])}" y2="${f1(b[1] - hpx)}" stroke="${c}" stroke-width="1.2"/>`,
  );
  const n = Math.max(1, Math.round(Math.hypot(x2 - x1, y2 - y1) * 2));
  for (let i = 0; i <= n; i++) {
    const x = a[0] + ((b[0] - a[0]) * i) / n,
      y = a[1] + ((b[1] - a[1]) * i) / n;
    o.push(
      `<line x1="${f1(x)}" y1="${f1(y)}" x2="${f1(x)}" y2="${f1(y - hpx - 1)}" stroke="${shade(c, 0.25)}" stroke-width="1.3"/>`,
    );
  }
}

function paintEdges(e: FlatEdges, P: Palette, o: string[]): void {
  const { parcels, outline, lands, hedges, decor } = e;
  // Kerbs between the plots.
  for (const g of parcelBoundaries(parcels.map((p) => p.tiles))) {
    const a = project(g.x1, g.y1),
      b = project(g.x2, g.y2);
    o.push(
      `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(b[0])}" y2="${f1(b[1])}" stroke="${alpha('#ECE9E2', 0.85)}" stroke-width="1.6"/>`,
    );
  }
  if (!hedges) return;
  const c = FENCING[decor?.hedge ?? ''] ?? C.fence;
  // D420: the fence decoration fills the panels; the default mesh keeps the fence's own.
  const fill = FENCE_INFILL[decor?.fence ?? ''];
  for (const [x0, y0, x1, y1] of outline?.hedges ?? []) paintFence(x0, y0, x1, y1, c, o, fill);
  for (const l of lands) {
    paintFence(l.x, l.y, l.x + l.w, l.y, c, o, fill);
    paintFence(l.x, l.y + l.h, l.x + l.w, l.y + l.h, c, o, fill);
    paintFence(l.x, l.y, l.x, l.y + l.h, c, o, fill);
    paintFence(l.x + l.w, l.y, l.x + l.w, l.y + l.h, c, o, fill);
  }
  // The entrance: a barrier across the road and the campus sign beside it.
  const g = decor?.gateAt;
  if (g && !isPlan()) {
    const sign = SIGNS[decor?.gate ?? ''] ?? '#3E6FB0';
    const along: [number, number] = [g.x - g.px, g.y - g.py];
    const side: [number, number] = [-along[1], along[0]];
    const bx = g.x + side[0] * 0.45,
      by = g.y + side[1] * 0.45;
    const L = lookOf(P);
    paintUnit(
      {
        x: bx - 0.2,
        y: by - 0.2,
        w: 0.4,
        d: 0.4,
        pieces: [
          {
            s: 'box',
            x: bx - 0.15,
            y: by - 0.06,
            w: 0.3,
            d: 0.12,
            z: 0,
            h: 0.42,
            c: sign,
            top: shade(sign, 0.1),
          },
          {
            s: 'rod',
            a: [bx, by, 0.2],
            b: [bx - side[0] * 0.9, by - side[1] * 0.9, 0.2],
            t: 0.04,
            c: L.night ? C.red : '#E4E7EA',
          },
        ],
      },
      P,
      o,
    );
  }
}

/** Snow falling over the campus in winter. */
export function paintWeather(b: Bounds, P: Palette, o: string[]): void {
  if (!P.S.frost) return;
  const rf = rnd(5);
  for (let i = 0; i < 120; i++) {
    const [cx, cy] = project(b.x - 1 + rf() * (b.w + 2), b.y - 1 + rf() * (b.h + 2));
    o.push(
      `<circle cx="${cx.toFixed(0)}" cy="${(cy - rf() * 90).toFixed(0)}" r="${(0.8 + rf() * 1.2).toFixed(1)}" fill="#ffffff" opacity=".55"/>`,
    );
  }
}

/* ---------- what stands on the land ---------- */

export const piece = (u: Unit): Prop => ({ k: 'piece', x: u.x, y: u.y, w: u.w, h: u.d, unit: u });

/** A plot's front corner, a little in, at this turn: where its sign and its staff stand. */
export function corner(b: Bounds, k: number, inset: number): Point {
  const cs: Point[] = [
    [b.x, b.y],
    [b.x + b.w, b.y],
    [b.x + b.w, b.y + b.h],
    [b.x, b.y + b.h],
  ];
  const c = cs[(frontCorner(b.x, b.y, b.w, b.h) + k) % 4] as Point;
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  return [c[0] + Math.sign(cx - c[0]) * inset, c[1] + Math.sign(cy - c[1]) * inset];
}

/* ---------- D422: the asset module's heroes, heart and store, as both renderers place them ---------- */

// D440: the theme's accent and money colours over the game's palette, as the 3D map takes them.
const A: Readonly<Record<string, string>> = { ...ASSET_PALETTE, ...THEME_MAP_COLOURS.compute };
const pal = (k: string): string => A[k] ?? k;

/**
 * D422, D424: where a producer's hero from the asset module stands, carried on a placeholder
 * unit: the flat map paints simple shapes there, the 3D map builds the module's own hero in its
 * place (`i` the producer's index, at its `level` and owned `units`).
 */
export interface AssetHero {
  i: number;
  level: number;
  units: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A unit that marks a hero of the asset module. */
export type AssetUnit = Unit & { asset?: AssetHero };

/** The roof's skin and the plant that names each producer, as the module's `computeRoof`. */
const ROOF: ReadonlyArray<readonly [string, string]> = [
  ['timber', 'tray'],
  ['plantWhite', 'tray'],
  ['plantWhite', 'tray'],
  ['plantWhite', 'trayBlue'],
  ['plantWhite', 'stack'],
  ['cool', 'trayBlue'],
  ['plantWhite', 'tray'],
  ['greenRoof', 'plantWhite'],
  ['cool', 'plantWhite'],
  ['stack', 'plantWhite'],
  ['plantWhite', 'tray'],
  ['trayBlue', 'plantWhite'],
  ['roofRed', 'plantWhite'],
  ['solar', 'solarCell'],
  ['cool', 'trayBlue'],
  ['plantWhite', 'stack'],
  ['greenRoof', 'tray'],
  ['glass', 'trayBlue'],
  ['plantWhite', 'cool'],
];

/**
 * Each hero's main building in the module's own frame (its middle, the plot's middle at 0):
 * [x, z, width, depth, height, wall, a hall that rises with the milestones]. Width 0: a
 * producer drawn by its own rule below (the cooling towers, the solar rows).
 */
const BODY: ReadonlyArray<readonly [number, number, number, number, number, string, boolean]> = [
  [-0.2, -0.3, 2.0, 1.4, 0.95, 'timber', false],
  [-0.5, -0.4, 1.5, 1.1, 0.6, 'shedWall', false],
  [0, 0, 2.4, 1.8, 0.92, 'cladding', false],
  [0, 0, 2.5, 2.1, 0.98, 'cladding', false],
  [-0.8, -0.8, 0.9, 0.7, 0.5, 'shedWall', false],
  [0, -0.2, 2.3, 1.6, 0.9, 'cladding', true],
  [0, -0.3, 2.3, 1.4, 1.4, 'cladding', true],
  [0, -0.15, 2.4, 1.7, 0.85, 'cladding', true],
  [0, 0, 0, 0, 0, 'cladding', false],
  [0, -0.3, 1.6, 0.45, 0.55, 'claddingDark', false],
  [0, 0, 2.4, 0.9, 0.7, 'cladding', false],
  [0, -0.4, 2.6, 1.3, 1.1, 'cladding', true],
  [0, -0.1, 2.2, 1.1, 0.6, 'cladding', false],
  [0, 0, 0, 0, 0, 'solar', false],
  [-0.3, -0.5, 1.6, 1.0, 0.7, 'cladding', true],
  [-0.05, -0.2, 2.4, 1.6, 0.85, 'white', true],
  [0, 0, 2.6, 2.2, 1.5, 'cladding', true],
  [0, 0, 2.0, 1.6, 0.78, 'glass', false],
  [-0.45, 0, 1.2, 1.4, 0.68, 'cladding', false],
];

/** A box piece by its middle. */
function boxAt(
  cx: number,
  cy: number,
  w: number,
  d: number,
  z: number,
  h: number,
  c: string,
  extra: { top?: string; glow?: boolean; roof?: boolean } = {},
): Piece {
  return { s: 'box', x: cx - w / 2, y: cy - d / 2, w, d, z, h, c, roof: true, ...extra };
}

/** The module's scale for a hero at a level: small, growing, then full size (`STAGE_SCALE`). */
const heroScale = (level: number): number => ([0.66, 0.82, 1][Math.min(2, level)] ?? 1) * 1.08;

/** D422, D424: a producer's hero as the flat map paints it, from the module's shapes. */
function heroUnit(p: IsoParcel, b: Bounds): AssetUnit | null {
  const content = getIdleContent('compute');
  const i = content.producers.findIndex((q) => q.id === p.id);
  const prod = content.producers[i];
  if (!prod) return null;
  const units = p.owned ?? 0;
  const level = assetLevel(
    units,
    prod.milestones.map((m) => m.owned),
  );
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  const s = heroScale(level);
  const [skin, plant] = ROOF[i % ROOF.length] ?? ['plantWhite', 'tray'];
  // The module's pieces keep the counts they were designed for (D424).
  const tier = level <= 1 ? level : units >= 25 ? 4 : units >= 10 ? 3 : 2;
  const qty = (base: number, max: number, per: number) =>
    Math.min(max, base + Math.floor(units / per));
  const at = (lx: number, lz: number): [number, number] => [cx + lx * s, cy + lz * s];
  const pieces: Piece[] = [];
  const body = BODY[i % BODY.length] ?? BODY[2]!;
  const [lx, lz, bw, bd, bh, wall, rises] = body;
  if (bw > 0) {
    const w = (rises && tier >= 4 ? bw * 1.12 : bw) * s,
      d = bd * s,
      h = (rises && tier >= 3 ? bh * 1.2 : bh) * s;
    const [x, y] = at(lx, lz);
    pieces.push(boxAt(x, y, w, d, 0, h, pal(wall), { top: pal(skin) }));
    pieces.push(
      boxAt(x, y + d / 2, w * 0.7, 0.03, h * 0.82, 0.05 * s, A.lampWarm!, { glow: true }),
    );
    pieces.push(boxAt(x, y + d * 0.22, w * 0.82, 0.12 * s, h, 0.06, pal(plant), { roof: false }));
    pieces.push(boxAt(x - w * 0.22, y - d * 0.2, w * 0.36, d * 0.3, h, 0.22 * s, A.plantWhite!));
  } else if (i === 8) {
    // The cooling towers, more of them as units are bought.
    const n = qty(2, 12, 12),
      cols = n > 6 ? 4 : n > 3 ? 3 : 2,
      k = n > 6 ? 0.55 : n > 3 ? 0.75 : 1;
    for (let j = 0; j < n; j++) {
      const [x, y] = at(
        -1.0 + (j % cols) * (2.0 / (cols - 1)) * 0.95,
        -0.9 + Math.floor(j / cols) * 0.95 * (n > 6 ? 0.7 : 1),
      );
      pieces.push({ s: 'tower', x, y, r: 0.52 * k * s, z: 0, h: 1.7 * k * s, c: A.cladding! });
    }
  } else {
    // The solar rows, more of them as units are bought.
    const n = qty(2, 6, 8);
    for (let r = 0; r < n; r++) {
      const [x, y] = at(0, -1.0 + r * (n > 1 ? 2.0 / (n - 1) : 0));
      pieces.push({
        s: 'panel',
        x: x - 1.15 * s,
        y: y - 0.16 * s,
        w: 2.3 * s,
        d: 0.32 * s,
        z: 0.25 * s,
        rise: 0.14 * s,
        c: A.solarCell!,
      });
    }
  }
  // The milestone pieces stand at full size round the hero (annex 10, second 25, mast 100).
  if (level >= 2 && units >= 10)
    pieces.push(boxAt(cx - 1.0, cy - 1.1, 0.55, 0.3, 0, 0.32, A.louvre!));
  if (level >= 2 && units >= 25) {
    pieces.push(boxAt(cx - 1.1, cy - 0.1, 0.6, 1.2, 0, 0.7, A.cladding!, { top: A.white }));
    pieces.push(boxAt(cx - 1.1, cy + 0.5, 0.5, 0.03, 0.4, 0.06, A.lampWarm!, { glow: true }));
  }
  if (level >= 2 && units >= 100) {
    pieces.push(boxAt(cx + 1.2, cy - 0.1, 0.12, 0.12, 0, 2.2, A.metal!, { roof: false }));
    pieces.push(boxAt(cx + 1.2, cy - 0.1, 0.1, 0.1, 2.2, 0.1, A.kerbRed!, { glow: true }));
  }
  return {
    x: b.x,
    y: b.y,
    w: b.w,
    d: b.h,
    pieces,
    id: p.id,
    asset: { i, level, units, x: b.x, y: b.y, w: b.w, h: b.h },
  };
}

/**
 * D426 (D366, D368): the rack-light cycle on the module's racks. The module's racks, cabinets
 * and bays carry static lights; over their fronts stands a thin door with the plot's own rack
 * lights (`marks`), which blink on the plot's work cycle in 3D exactly as the old racks did, and
 * show as lit on the flat map. Each entry: a row of doors in the module's own frame, [x of the
 * first, z of their front, how many, spacing, width, height], for the producers with racks.
 */
function rackRows(
  i: number,
  units: number,
): Array<[number, number, number, number, number, number]> {
  const qty = (base: number, max: number, per: number) =>
    Math.min(max, base + Math.floor(units / per));
  if (i === 1) {
    const n = qty(1, 6, 8);
    return Array.from({ length: Math.ceil(n / 2) }, (_, r) => {
      const k = Math.min(2, n - r * 2);
      return [0.45, -0.7 + r * 0.55 + 0.15, k, 0.4, 0.28, 0.55] as [
        number,
        number,
        number,
        number,
        number,
        number,
      ];
    });
  }
  if (i === 2) {
    const rows = qty(1, 4, 8),
      n = qty(3, 9, 4);
    return Array.from({ length: rows }, (_, j) => {
      const z = rows > 1 ? -0.65 + j * (1.4 / (rows - 1)) : -0.65;
      return [-1.0, z + 0.13, n, 0.22, 0.18, 0.5] as [
        number,
        number,
        number,
        number,
        number,
        number,
      ];
    });
  }
  if (i === 3) {
    const nc = qty(2, 10, 6);
    return Array.from({ length: Math.ceil(nc / 5) }, (_, j) => {
      const k = Math.min(5, nc - j * 5);
      return [-0.9, -0.45 + j * 0.9 + 0.15, k, 0.45, 0.32, 0.64] as [
        number,
        number,
        number,
        number,
        number,
        number,
      ];
    });
  }
  // The storage hall's drive bays and the GPU hall's node doors, on the hall's front wall.
  if (i === 5) return [[-0.75, 0.6, 3, 0.36, 0.3, 0.5]];
  if (i === 7) return [[-0.8, 0.7, 4, 0.34, 0.28, 0.5]];
  return [];
}

/** D426: the rack lights' colour lit, by producer, as the old racks had them. */
const RACK_ON: Record<number, string> = {
  1: C.green,
  2: C.green,
  3: C.blue,
  5: C.amber,
  7: C.cyan,
};

function rackLights(p: IsoParcel, i: number, units: number, b: Bounds, s: number): Unit | null {
  const rows = rackRows(i, units);
  if (!rows.length) return null;
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  const pieces: Piece[] = [];
  for (const [x0, zf, n, step, w, h] of rows)
    for (let k = 0; k < n; k++) {
      const x = cx + (x0 + k * step) * s;
      pieces.push({
        s: 'box',
        x: x - (w * s) / 2,
        y: cy + zf * s,
        w: w * s,
        d: 0.02,
        z: 0.02,
        h: h * s,
        c: C.rack,
        marks: {
          kind: 'led',
          rows: Math.max(2, Math.round((h * s) / 0.18)),
          cols: 2,
          c: C.rackLed,
          on: sweepOn(p) ? C.cyan : (RACK_ON[i] ?? C.green),
          lit: litShare(p),
          blink: true,
          busy: busyShare(p),
          front: true,
          z0: 0.12,
          z1: 0.9,
        },
      });
    }
  return { x: b.x, y: b.y, w: b.w, d: b.h, pieces, id: p.id };
}

/** D422: the heart's stage from its drivers, as the module reads them. */
function heartStage(h: Heart): number {
  const byP = h.producers >= 15 ? 2 : h.producers >= 7 ? 1 : 0;
  const byY = h.years >= 7 ? 2 : h.years >= 3 ? 1 : 0;
  return Math.max(byP, byY);
}

/**
 * D422, D423: the heart as the flat map paints it, from the same plan as the 3D map's: the
 * first data hall named in light, the second hall and its cooling towers from the second stage,
 * the tall hall and its sky bridge at the third, the glass dome from the second generation.
 * Laid out on the module's 7 by 7 plot, scaled evenly onto the yard, mirrored with the world.
 */
function heartUnit(h: Heart): Unit {
  const plan = heartPlan(h);
  const k = plan.size / 7,
    m = h.dir;
  const st = heartStage(h);
  const X = (lx: number) => plan.x + (3.5 + m * lx) * k,
    Y = (lz: number) => plan.y + (3.5 + lz) * k;
  const box = (
    lx: number,
    lz: number,
    w: number,
    d: number,
    z: number,
    hh: number,
    c: string,
    extra: { top?: string; glow?: boolean; roof?: boolean } = {},
  ): Piece => boxAt(X(lx), Y(lz), w * k, d * k, z * k, hh * k, c, extra);
  const H = [1.0, 1.1, 1.2][st] ?? 1;
  const hall = (lx: number, lz: number, w: number, d: number, hh: number, roof: string) => {
    pieces.push(box(lx, lz, w, d, 0, hh, A.cladding!, { top: pal(roof) }));
    pieces.push(box(lx, lz + d / 2, w * 0.7, 0.04, hh * 0.84, 0.06, A.lampWarm!, { glow: true }));
  };
  const pieces: Piece[] = [
    box(0, 0, 7, 7, 0, 0.03, st >= 2 ? A.stoneCap! : A.verge!, { roof: false }),
  ];
  hall(-0.6, 1.4, 4.6, 1.8, H, 'trayBlue');
  pieces.push(box(-0.6, 2.32, 2.4, 0.04, H * 0.6, 0.2, A.screen!, { glow: true }));
  if (st >= 1) {
    hall(-1.1, -1.4, 3.4, 1.6, H, 'greenRoof');
    for (const lx of [1.6, 2.4])
      pieces.push({
        s: 'tower',
        x: X(lx),
        y: Y(-1.6),
        r: 0.4 * k,
        z: 0,
        h: 1.3 * k,
        c: A.cladding!,
      });
  }
  if (st >= 2) {
    hall(2.0, 1.2, 2.7, 2.4, 1.92, 'greenRoof');
    pieces.push(box(0.8, 1.4, 2.2, 0.6, 1.9, 0.35, A.glass!, { top: A.white }));
    for (let i = 0; i < 4; i++)
      pieces.push(box(-2.6 + i * 1.3, 3.0, 0.9, 0.9, 0, 0.3, A.stoneDark!));
  }
  if (h.generations >= 2) {
    pieces.push({ s: 'cyl', x: X(-2.2), y: Y(-2.0), r: 0.9 * k, z: 0, h: 1.2 * k, c: A.glass! });
    pieces.push(box(-2.2, -2.0, 1.0, 1.0, 0.3, 0.1, A.lampWarm!, { glow: true }));
  }
  return { x: plan.x, y: plan.y, w: plan.size, d: plan.size, pieces };
}

/**
 * D423: the store's bays beside the heart, one for every storage upgrade, drawn the same in both
 * maps: a small hall each, its louvres and a lit strip to the front.
 */
export function storeUnits(h: Heart): Unit[] {
  return heartPlan(h).bays.map((b) => {
    const cx = b.x + b.w / 2,
      cy = b.y + b.h / 2;
    return {
      x: b.x,
      y: b.y,
      w: b.w,
      d: b.h,
      pieces: [
        boxAt(cx, cy, b.w, b.h, 0, 0.6, A.cladding!, { top: A.white }),
        boxAt(cx, cy, b.w + 0.01, b.h + 0.01, 0.3, 0.16, A.louvre!),
        boxAt(cx, b.y + b.h, b.w * 0.7, 0.03, 0.5, 0.05, A.lampWarm!, { glow: true }),
      ],
    };
  });
}

/**
 * D422, D423: a building's units, the heart's in place of the home's: the 3D map builds the
 * module's heart and takes only the store's bays from here (`flat` off); the flat map paints both.
 */
export function computeBuildingUnits(b: IsoBuildingAt, flat = true): Unit[] {
  if (!b.heart) return buildingUnits(b);
  return flat ? [heartUnit(b.heart), ...storeUnits(b.heart)] : storeUnits(b.heart);
}

/** D422: each plot's own edge on the flat map, as the module's: a green verge, a yellow kerb. */
function paintPlotEdges(parcels: IsoParcel[], P: Palette, o: string[]): void {
  // D440: the verge under winter's snow, as far as it has come.
  const verge = mix(A.verge!, SNOW.colour, WINTER_COVER.grass * (P.snow ?? 0));
  if (isPlan()) return;
  for (const p of parcels) {
    if (p.t !== 'field' || !p.id || !p.tiles.length) continue;
    const b = unionBounds(p.tiles);
    const t = 0.12;
    o.push(`<path d="${polyPath(rectPts(b.x, b.y, b.w, b.h))}" fill="${verge}"/>`);
    for (const [x, y, w, d] of [
      [b.x, b.y, b.w, t],
      [b.x, b.y + b.h - t, b.w, t],
      [b.x, b.y, t, b.h],
      [b.x + b.w - t, b.y, t, b.h],
    ] as const)
      o.push(`<path d="${polyPath(rectPts(x, y, w, d))}" fill="${A.kerbYellow}"/>`);
  }
}

/** Everything standing on a parcel, placed for both renderers. */
export function computeProps(
  p: IsoParcel,
  showSigns: boolean,
  _L: Look = { night: false, winter: false },
): Prop[] {
  const out: Prop[] = [];
  const b = unionBounds(p.tiles);
  if (!b.w) return out;
  if (p.t === 'field' && p.id) {
    // D422: the producer's hero from the asset module (a placeholder the flat map paints simply).
    const hero = heroUnit(p, b);
    const a = hero?.asset;
    const lights = a ? rackLights(p, a.i, a.units, b, heroScale(a.level)) : null;
    const units: Unit[] = hero ? (lights ? [hero, lights] : [hero]) : [];
    for (const u of units) out.push(piece(u));
    if (p.sign && showSigns) {
      const at = corner(b, 0, 0.12);
      out.push({ k: 'sign', x: at[0], y: at[1], text: p.sign });
    }
    // D268: a member of staff by the plot once a hand is hired, more with the milestones.
    const staff = (p.hand ? 1 : 0) + Math.min(2, Math.floor((p.tier ?? 0) / 5));
    for (let i = 0; i < staff; i++) {
      const at = corner(b, 3 - (i % 2) * 2, 0.08 + i * 0.12);
      out.push({ k: 'worker', x: at[0], y: at[1], ...(p.workers ? { look: p.workers } : {}) });
    }
    if (p.burst)
      out.push({ k: 'burst', x: b.x + b.w / 2, y: b.y + b.h / 2, r: Math.max(b.w, b.h) / 2 });
    // D345: the ready badge, with the compute producer's own icon.
    if (p.ready) {
      const [fx, fy] = corner(b, 0, 0.02);
      out.push(
        piece({
          x: fx - 0.01,
          y: fy - 0.01,
          w: 0.02,
          d: 0.02,
          pieces: [],
          badge: {
            x: b.x + b.w / 2,
            y: b.y + b.h / 2,
            id: p.id,
            icon: `producers_${p.id}`,
            lift: units.reduce((t, u) => Math.max(t, unitTop(u)), 0.5) + 0.45,
          },
        }),
      );
    }
    return out;
  }
  if (p.t === 'meadow' && p.mark) {
    for (const u of markUnits(p.mark, b.x + b.w / 2, b.y + b.h / 2)) out.push(piece(u));
    return out;
  }
  if (p.t === 'meadow' && p.ground) {
    // D327: a monument ground: each later festival's mark, small, in rows.
    const marks = p.ground.marks.slice(0, 8);
    marks.forEach((m, i) => {
      const x = b.x + 0.5 + (i % 4) * ((b.w - 1) / 3),
        y = b.y + 0.6 + Math.floor(i / 4) * (b.h - 1.2);
      out.push(
        piece({
          x: x - 0.2,
          y: y - 0.2,
          w: 0.4,
          d: 0.4,
          pieces: [
            {
              s: 'box',
              x: x - 0.15,
              y: y - 0.1,
              w: 0.3,
              d: 0.2,
              z: 0,
              h: 0.12,
              c: '#9EA19C',
              roof: true,
            },
            {
              s: 'panel',
              x: x - 0.12,
              y: y - 0.07,
              w: 0.24,
              d: 0.14,
              z: 0.12,
              rise: 0.08,
              c: m === 'beacon' ? C.cyan : C.copper,
            },
          ],
        }),
      );
    });
    return out;
  }
  if (p.t === 'meadow') {
    // The grass verges: a few planted trees.
    const r = rnd(b.x * 31 + b.y * 17 + 3);
    for (const [x, y] of scatter(p.tiles, r, Math.round(p.tiles.length * 0.12), 0.3)) {
      const s = 0.8 + r() * 0.4;
      out.push(
        piece({
          x: x - 0.15,
          y: y - 0.15,
          w: 0.3,
          d: 0.3,
          pieces: [
            { s: 'cyl', x, y, r: 0.035, z: 0, h: 0.22 * s, c: '#6B5A44' },
            { s: 'ball', x, y, r: 0.22 * s, z: 0.14 * s, c: '#3F7A44' },
          ],
        }),
      );
    }
    return out;
  }
  // D422, D423: the yard holds the heart, so no cars are parked on it.
  return out;
}

/** Paint one standing thing: a unit of pieces, or one of the shared props (staff, sign, badge). */
function paintComputeProp(s: Prop, P: Palette, o: string[]): void {
  if (s.k === 'piece') return paintUnit(s.unit as Unit, P, o);
  if (s.k === 'build') {
    for (const u of computeBuildingUnits(s)) paintUnit(u, P, o);
    return;
  }
  paintProp(s, P, o);
}

/** Every unit standing on the campus: the buildings', the plots' and the marks'. */
function allUnits(buildings: IsoBuildingAt[], parcels: IsoParcel[]): Unit[] {
  const L: Look = { night: false, winter: false };
  const out = buildings.flatMap((b) => computeBuildingUnits(b));
  for (const p of parcels)
    for (const s of computeProps(p, false, L)) if (s.k === 'piece') out.push(s.unit as Unit);
  return out;
}

/** D350: how tall the campus stands, in world units, for the 3D camera. */
export function campusTop(buildings: IsoBuildingAt[], parcels: IsoParcel[]): number {
  let t = 1.2;
  for (const u of allUnits(buildings, parcels)) t = Math.max(t, unitTop(u));
  return t + 0.15;
}

/* ---------- the kit ---------- */

/** D349: the compute theme's map kit. */
export const computeKit: MapKit = {
  id: 'compute',
  plots: computePlots(),
  chainBuildings: computeChains(),
  landscape: computeLandscape,
  edgeWater: false,
  // D353: a worked plot's racks light as one while it is "ripe", on the farm's own cycle.
  fieldCycle: FIELD_CYCLE,
  palette: computePalette,
  phaseTime: (phase) => PHASE_TIME[phase] ?? 'morning',
  light: (phase) => PHASE_LIGHT[phase] ?? PHASE_LIGHT.day!,
  goodWeather: GOOD_WEATHER,
  sky: (P, phase) => {
    const light = PHASE_LIGHT[phase] ?? PHASE_LIGHT.day!;
    return mix(P.far(P.pasture), light.haze, light.hazeT);
  },
  // D345: the shared badge's colours; the compute icon when a row has none of its own.
  readyBadge: { ...READY_BADGE, fallback: 'currency_tokens' },
  // D422, D423: the heart on the yard, the store part of it; each plot's units counted.
  heart: true,
  dress: (p, { owned }) => {
    p.owned = owned;
  },
  top: campusTop,
  tall: true,
  flat: {
    scenery: paintCountry,
    ground: paintGround,
    fieldJob: () => [],
    machine: () => {},
    edges: paintEdges,
    plotEdges: paintPlotEdges,
    props: (p, showSigns) => computeProps(p, showSigns),
    prop: paintComputeProp,
    standing: (buildings, parcels): Standing[] =>
      allUnits(buildings, parcels).map((u) => ({
        x: u.x,
        y: u.y,
        w: u.w,
        h: u.d,
        z: unitTop(u) * ZPX + 6,
      })),
    weather: paintWeather,
    // D349: vapour off the cooling towers, else off the warehouse's roof plant.
    smoke: (buildings, parcels = []) => {
      const out: Puff[] = [];
      for (const p of parcels) {
        if (p.id !== 'cooling_towers' || out.length >= 9) continue;
        // D422: off the tops of the hero's own towers.
        const towers = computeProps(p, false)
          .flatMap((q) => (q.k === 'piece' ? (q.unit as Unit).pieces : []))
          .filter((q): q is Extract<Piece, { s: 'tower' }> => q.s === 'tower');
        for (const q of towers.slice(0, 3)) {
          const [cx, cy] = project(q.x, q.y, (q.z + q.h) * ZPX + 4);
          [0, 3.1, 6.2].forEach((delay, i) =>
            out.push({ cx: cx + i * 2, cy, r: 7 + i * 2, delay: delay + (q.x % 3) }),
          );
        }
      }
      if (!out.length) {
        const barn = buildings.find((b) => b.kind === 'barn');
        if (barn) {
          const [cx, cy] = project(barn.x + barn.w * 0.5, barn.y + barn.h * 0.5, 0.7 * ZPX);
          [0, 3.1, 6.2].forEach((delay, i) => out.push({ cx, cy, r: 4 + i * 1.5, delay }));
        }
      }
      return out;
    },
    // The cooling pond's ripples.
    ripples: (parcels) => {
      const out: Ripple[] = [];
      for (const pc of parcels) if (pc.t === 'pond') out.push(...pondRipples(pc.tiles));
      return out;
    },
  },
};
